import { GET_COMPANY } from "@apiUtils";
import type { IPagedResponse } from "@atlas/sdk";
import { mutationFetch } from "@atlas/sdk";
import type { ISubscriptionUsage } from "@models";
import {
  type InfiniteData,
  type UseInfiniteQueryOptions,
  useInfiniteQuery,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const SUBSCRIPTION_USAGE_INFINITE_KEY = "subscriptionUsageInfiniteKey";

export interface ISubscriptionUsageRequest {
  companyUuid: string;
  page?: number;
  size?: number;
}

export const getSubscriptionUsageInfiniteKey = (
  request: ISubscriptionUsageRequest,
  basePath?: string,
) => [SUBSCRIPTION_USAGE_INFINITE_KEY, request, basePath] as const;

export const useSubscriptionUsageInfinite = (
  companyUuid: string | undefined,
  basePath?: string,
  params?: { size?: number; page?: number },
  options?: Partial<
    UseInfiniteQueryOptions<
      IPagedResponse<ISubscriptionUsage>, // 1. TQueryFnData
      AxiosError, // 2. TError
      InfiniteData<IPagedResponse<ISubscriptionUsage>>, // 3. TData
      ReturnType<typeof getSubscriptionUsageInfiniteKey>, // 4. TQueryKey
      number // 5. TPageParam
    >
  >,
) => {
  const size = params?.size ?? 10;
  const page = params?.page ?? 0;

  const initialRequest: ISubscriptionUsageRequest = {
    companyUuid: companyUuid ?? "",
    page,
    size,
  };

  return useInfiniteQuery({
    queryKey: getSubscriptionUsageInfiniteKey(initialRequest, basePath),
    queryFn: async ({ pageParam }) => {
      const path = `${GET_COMPANY}/${companyUuid}/subscription-usage`;
      const url = basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;

      return mutationFetch<IPagedResponse<ISubscriptionUsage>>({
        url,
        method: "GET",
        params: { page: pageParam, size },
      });
    },
    initialPageParam: page,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.number + 1;
      return nextPage < lastPage.totalPages ? nextPage : undefined;
    },
    getPreviousPageParam: (firstPage) =>
      firstPage.number > 0 ? firstPage.number - 1 : undefined,
    enabled: !!companyUuid,
    ...options,
  });
};
